import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import {
  EmailTemplateModel,
  EmailTemplateRequest,
  EmailThemeRequest,
  IEmailTemplateRequest,
} from "services/KnowfullyAdminClient";
import Field from "components/Field";
import useGlobalContext from "hooks/globalData";
import { FORM_ERROR } from "final-form";
import translateError from "utils/translateError";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";
import FieldRichText from "components/FieldRichText";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import contextStore from "stores/contextStore";
import FormTextField from "components/FormTextField";

const styles = {
  feedback: {
    width: "100%",
    margin: "15px 0 0 15px",
  },
};

const { Form } = withTypes<EmailTemplateRequest>();

type EmailTemplateFormProps = {
  emailTemplate?: EmailTemplateModel;
  submitLabel: string;
};

const EmailTemplateForm = ({
  emailTemplate,
  submitLabel,
}: EmailTemplateFormProps) => {
  const { formatMessage: t } = useIntl();
  const {
    emailTemplateDetailsUrl,
    emailTemplatesUrl,
    notificationService,
    emailService,
  } = useGlobalContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { organizationId } = contextStore.useState();

  const init = {
    ...emailTemplate,
    organizationId: emailTemplate?.organizationId ?? organizationId,
  };

  const onSubmit = async (values: IEmailTemplateRequest) => {
    try {
      const response = await emailService.saveEmailTemplate(
        new EmailTemplateRequest(values),
      );
      queryClient.invalidateQueries("emailTemplates");
      queryClient.invalidateQueries("getEmailTemplate");

      navigate(emailTemplateDetailsUrl(response.id));

      return response;
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  const themeQuery = useQuery(
    ["getEmailThemeByOrganization", init.organizationId],
    () =>
      notificationService.getEmailThemeByOrganization(
        EmailThemeRequest.fromJS({
          organizationId: init.organizationId,
        }),
      ),
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({
        handleSubmit,
        submitting,
        submitError,
        values,
        form,
        dirty,
      }) => (
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <ContextOrganizationField
                values={values}
                formChange={form.change}
                mode={ContextOrganizationFieldMode.EditDisabled}
              />
              <Grid item xs={12}>
                <FormTextField
                  name="name"
                  label={t({ id: "templateName" })}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <FormTextField
                  name="subject"
                  label={t({ id: "subject" })}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  disabled
                  fullWidth
                  required
                  value={
                    themeQuery?.data?.data?.inheritedOrganizationName ?? ""
                  }
                  variant="standard"
                  label={t({ id: "emailTheme" })}
                />
              </Grid>

              <Grid item xs={12}>
                <FormTextField
                  name="senderName"
                  label={t({ id: "senderName" })}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <FormTextField
                  name="senderEmail"
                  label={t({ id: "senderEmail" })}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <FormTextField name="ccEmails" label={t({ id: "ccEmails" })} />
              </Grid>

              <Grid item xs={12}>
                <FormTextField
                  name="bccEmails"
                  label={t({ id: "bccEmails" })}
                />
              </Grid>

              <Grid item xs={12}>
                <Field name="body" required>
                  {({ input, meta }) => {
                    return (
                      <FieldRichText
                        input={input}
                        meta={meta}
                        cleanupClasses={false}
                        init={{
                          height: 500,
                        }}
                      />
                    );
                  }}
                </Field>
              </Grid>

              {submitError && (
                <Box sx={styles.feedback}>
                  <Alert severity="error">
                    {JSON.stringify(submitError, null, 2)}
                  </Alert>
                </Box>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button
              disabled={submitting}
              onClick={() =>
                navigate(
                  emailTemplate?.id
                    ? emailTemplateDetailsUrl(emailTemplate.id)
                    : emailTemplatesUrl(),
                )
              }
            >
              {t({ id: "cancelButton" })}
            </Button>

            <Button
              type="submit"
              color="primary"
              disabled={submitting || !dirty}
            >
              {submitLabel}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default EmailTemplateForm;
